/**
 * Theme validation, field by field (architecture.md "Untrusted content": no CSS text is ever executed).
 * A port of design/reboot/themes.js `normalize()`, plus the import/export file format
 * `{ format: 'digester-theme', version: 1, theme }` (contracts/settings.ts `ThemeFile`).
 */

import { DEFAULT_CUSTOM_THEME, DigesterError, THEME_COLOR_KEYS, type ThemeDefinition, type ThemeFile, type ThemeFont } from '@digester/core';

/** [key, label, default, min, max, step, unit] — the prototype's numberSpecs, same order. */
export const THEME_NUMBER_SPECS = [
  ['uiSize', 'Interface text', 15, 14, 20, 1, 'px'],
  ['readingSize', 'Article text', 18, 15, 28, 1, 'px'],
  ['headingSize', 'Article headings', 28, 24, 44, 1, 'px'],
  ['uiLineHeight', 'Interface line height', 1.5, 1.2, 2, 0.05, '×'],
  ['readingLineHeight', 'Article line height', 1.8, 1.3, 2.4, 0.05, '×'],
  ['letterSpacing', 'Letter spacing', 0, -0.5, 1, 0.05, 'px'],
  ['rowPadding', 'Message row padding', 16, 8, 32, 1, 'px'],
  ['readerPadding', 'Reading margins', 34, 18, 72, 2, 'px'],
  ['sectionGap', 'Section spacing', 26, 14, 48, 2, 'px'],
  ['controlHeight', 'Control height', 42, 38, 56, 2, 'px'],
  ['toolbarHeight', 'Toolbar height', 72, 64, 96, 2, 'px'],
  ['radius', 'Corner radius', 8, 0, 20, 1, 'px'],
  ['borderWidth', 'Border width', 1, 0, 3, 1, 'px'],
  ['iconSize', 'Action icons', 20, 16, 26, 1, 'px'],
  ['navWidth', 'Sidebar width', 190, 160, 250, 5, 'px'],
  ['listWidth', 'Message list width', 360, 300, 460, 10, 'px'],
  ['detailsWidth', 'Details width', 292, 250, 360, 2, 'px'],
  ['articleWidth', 'Article measure', 700, 480, 900, 20, 'px'],
] as const;

export type ThemeNumberKey = (typeof THEME_NUMBER_SPECS)[number][0];

/** [key, label] of the 18 colour roles, the prototype's order and wording. */
export const THEME_COLOR_LABELS: ReadonlyArray<readonly [(typeof THEME_COLOR_KEYS)[number], string]> = [
  ['page', 'App background'], ['nav', 'Sidebar'], ['list', 'Message list'], ['reader', 'Reading surface'], ['details', 'Details surface'],
  ['surface', 'Controls & cards'], ['surface-hover', 'Hover background'], ['border', 'Dividers'], ['border-light', 'Control borders'],
  ['text', 'Headings'], ['body', 'Article text'], ['muted', 'Labels & links'], ['dim', 'Secondary text'], ['accent', 'Accent text'],
  ['accent-fill', 'Primary buttons'], ['accent-bg', 'Active controls'], ['selected', 'Selected message'], ['green', 'Success'],
];

export const THEME_FONTS: ReadonlyArray<readonly [ThemeFont, string]> = [
  ['Inter', 'Inter'], ['system-ui', 'System font'], ['Verdana', 'Verdana'], ['Georgia', 'Georgia'], ['ui-serif', 'System serif'],
  ['ui-monospace', 'System monospace'], ['custom', 'Installed font…'],
];

const FONT_SET: ReadonlySet<string> = new Set(THEME_FONTS.map(([f]) => f));
const HEX = /^#[\da-f]{6}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Installed font names: no ; { } < > \ (and no quotes, which would end the CSS string). */
export function sanitizeFamily(value: unknown): string {
  return String(value ?? '').replace(/[;{}<>\\"']/g, '').slice(0, 100);
}

/** Any input → a complete, safe ThemeDefinition (unknown fields dropped, numbers clamped, bad colours defaulted). */
export function normalizeTheme(value: unknown): ThemeDefinition {
  const d = DEFAULT_CUSTOM_THEME;
  const v = isRecord(value) ? value : {};
  const colorsIn = isRecord(v.colors) ? v.colors : {};
  const colors = { ...d.colors };
  for (const key of THEME_COLOR_KEYS) {
    const c = colorsIn[key];
    if (typeof c === 'string' && HEX.test(c)) colors[key] = c.toLowerCase();
  }
  const out: ThemeDefinition = {
    ...d,
    name: typeof v.name === 'string' && v.name.trim() !== '' ? v.name.trim().slice(0, 60) : d.name,
    base: v.base === 'light' ? 'light' : 'dark',
    uiFont: typeof v.uiFont === 'string' && FONT_SET.has(v.uiFont) ? (v.uiFont as ThemeFont) : d.uiFont,
    readingFont: typeof v.readingFont === 'string' && FONT_SET.has(v.readingFont) ? (v.readingFont as ThemeFont) : d.readingFont,
    uiFamily: sanitizeFamily(v.uiFamily),
    readingFamily: sanitizeFamily(v.readingFamily),
    shadows: typeof v.shadows === 'boolean' ? v.shadows : d.shadows,
    colors,
  };
  for (const [key, , fallback, min, max] of THEME_NUMBER_SPECS) {
    const n = Number(v[key]);
    out[key] = Math.min(max, Math.max(min, Number.isFinite(n) ? n : fallback));
  }
  return out;
}

/** The CSS font-family value of a theme font (the prototype's `family()`). */
export function fontFamily(font: ThemeFont, custom: string): string {
  if (font === 'custom') return `"${sanitizeFamily(custom)}",system-ui,sans-serif`;
  if (font === 'Inter') return 'Inter,system-ui,sans-serif';
  if (font === 'Georgia') return 'Georgia,serif';
  if (font === 'Verdana') return 'Verdana,sans-serif';
  return font;
}

export function exportThemeFile(theme: ThemeDefinition): ThemeFile {
  return { format: 'digester-theme', version: 1, theme: normalizeTheme(theme) };
}

/** Parse an imported file's text. Rejects invalid_input for anything that is not a Digester theme. */
export function parseThemeFile(text: string, maxBytes = 100_000): ThemeDefinition {
  if (text.length > maxBytes) throw new DigesterError('invalid_input', 'Theme file is too large');
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new DigesterError('invalid_input', 'Choose a Digester theme JSON file');
  }
  if (!isRecord(data) || data.format !== 'digester-theme' || !isRecord(data.theme) || !isRecord(data.theme.colors)) {
    throw new DigesterError('invalid_input', 'Choose a Digester theme JSON file');
  }
  return normalizeTheme(data.theme);
}

/** WCAG contrast ratio of two #rrggbb colours (the prototype's `contrast()`). */
export function contrastRatio(foreground: string, background: string): number {
  const luminance = (hex: string): number => {
    const channels = (hex.slice(1).match(/../g) ?? ['00', '00', '00']).map((c) => parseInt(c, 16) / 255).map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    return (channels[0] ?? 0) * 0.2126 + (channels[1] ?? 0) * 0.7152 + (channels[2] ?? 0) * 0.0722;
  };
  const a = luminance(foreground);
  const b = luminance(background);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
